import React, { useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { isDesmet, isSapDPL } from 'Utils/index';
import { FiEdit2, FiSave, FiDownload, FiUpload } from 'react-icons/fi';
import { FaGasPump } from 'react-icons/fa';
import { UPDATE_DPH_CALCULATION_MASTER, DOWNLOAD_DPH_CALCULATION_MASTER, UPLOAD_DPH_CALCULATION_MASTER } from 'Constants/actionConstants';
import { getUser } from 'Utils/storage';

const LOI_BADGE = {
   display: 'inline-block',
   padding: '2px 8px',
   borderRadius: 4,
   fontSize: 11,
   fontWeight: 600,
   marginRight: 4,
};

const HEADER_CELL = {
   fontWeight: 700,
   fontSize: 12,
   padding: '8px 4px',
   alignSelf: 'center',
};

const DATA_CELL = {
   padding: '14px 4px',
   alignSelf: 'start',
   fontSize: 12,
   minWidth: 0,
};

// Full-width separator that spans every column — avoids the staggered border
// problem that occurs when per-cell border-bottom are at different heights.
const ROW_SEPARATOR = {
   gridColumn: '1 / -1',
   borderTop: '1px solid #e0e0e0',
   height: 0,
   padding: 0,
   margin: 0,
};

const BID_COL_MIN = 160;

const DPH_FIELDS = [
   { field: 'prev_dph_rate', label: 'Prev DPH Rate' },
   { field: 'current_dph_rate', label: 'Current DPH Rate' },
   { field: 'hsd_grace', label: 'Inc/Dec Price' },
   { field: 'mileage', label: 'Mileage' },
   { field: 'carrying_capacity', label: 'Carrying Capacity' },
   { field: 'rtkm', label: 'RTKM' },
   { field: 'density', label: 'Density' },
];

const getDphFromRfqTrip = (rfqTrip) => {
   const d = rfqTrip?.dphDetails || {};
   return {
      dph_id:                    d.dph_id                    ?? '',
      is_hsd_enabled:            d.is_hsd_enabled            ?? false,
      prev_dph_rate:             d.prev_dph_rate             ?? '',
      current_dph_rate:          d.current_dph_rate          ?? '',
      hsd_grace:                 d.hsd_grace                 ?? '',
      mileage:                   d.mileage                   ?? '',
      carrying_capacity:         d.carrying_capacity         ?? '',
      rtkm:                      d.rtkm                      ?? '',
      density:                   d.density                   ?? '',
      company_id:                d.company_id                ?? '',
      product_category_multiple: d.product_category_multiple ?? [],
      rfq_number:                d.rfq_number                ?? '',
      bound_type:                d.bound_type                ?? '',
      contract_start_date:       d.contract_start_date       ?? '',
      contract_end_date:         d.contract_end_date         ?? '',
   };
};

const AdminBidsTable = ({ trips = [], rfqTrips = [], rfq = null, editedPrices = {}, onPriceChange, onUploadSuccess }) => {
   const dispatch = useDispatch();
   const uploadInputRef = useRef(null);
   const rfqTripById = React.useMemo(
      () => Object.fromEntries((rfqTrips).map(t => [t.id, t])),
      [rfqTrips]
   );

   const [openDph, setOpenDph] = useState({});
   const [dphData, setDphData] = useState({});
   const [dphEditMode, setDphEditMode] = useState({});
   const [dphSaving, setDphSaving] = useState({});

   React.useEffect(() => {
      if (!rfqTrips.length) return;
      setDphData(
         Object.fromEntries(
            trips.map((trip, idx) => {
               const rfqTrip = rfqTripById[trip.id] || rfqTripById[trip.tripId];
               return [idx, getDphFromRfqTrip(rfqTrip)];
            })
         )
      );
   }, [rfqTrips]);

   const handleDownloadDph = () => {
      const rfqId = (rfqTrips[0] || {}).rfqId || '';
      const companyId = (getUser() || {}).companyId || '';
      dispatch({
         type: DOWNLOAD_DPH_CALCULATION_MASTER,
         params: { rfqId, companyId },
         onSuccess: (presignedUrlData) => {
            if (presignedUrlData && presignedUrlData.url) {
               const a = document.createElement('a');
               a.href = presignedUrlData.url;
               a.download = presignedUrlData.name || 'DPHCalculation.xlsx';
               a.click();
            }
         },
      });
   };

   const handleUploadDph = (e) => {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      const rfqId = (rfqTrips[0] || {}).rfqId || '';
      const companyId = (getUser() || {}).companyId || '';
      dispatch({
         type: UPLOAD_DPH_CALCULATION_MASTER,
         file,
         params: { rfqId, companyId },
         onSuccess: () => {
            if (uploadInputRef.current) uploadInputRef.current.value = '';
            if (onUploadSuccess) onUploadSuccess();
         },
      });
   };

   const toggleDph = (idx) => setOpenDph((p) => ({ ...p, [idx]: !p[idx] }));
   const updateDphField = (idx, field, value) =>
      setDphData((p) => ({ ...p, [idx]: { ...(p[idx] || {}), [field]: value } }));
   const enterDphEdit = (idx) => setDphEditMode((p) => ({ ...p, [idx]: true }));

   const saveDph = (idx) => {
      console.log("rfq",rfq)
      const fields = dphData[idx] || {};
      const trip = trips[idx];
      const rfqTrip = rfqTripById[trip?.id] || rfqTripById[trip?.tripId] || {};
      const payload = {
         rfq_id:                    rfqTrip.rfqId           ?? '',
         trip_id:                   rfqTrip.id              ?? '',
         route_code:                rfqTrip.routeCode       ?? '',
         from:                      rfqTrip.from            ?? '',
         to:                        rfqTrip.to              ?? '',
         vehicle_type:              rfqTrip.vehicleTypeName ?? '',
         dph_id:                    fields.dph_id,
         is_hsd_enabled:            fields.is_hsd_enabled,
         prev_dph_rate:             fields.prev_dph_rate ?? '',
         current_dph_rate:          fields.current_dph_rate,
         hsd_grace:                 fields.hsd_grace,
         mileage:                   fields.mileage,
         carrying_capacity:         fields.carrying_capacity,
         rtkm:                      fields.rtkm,
         density:                   fields.density,
         company_id:                rfq?.companyId,
         product_category_multiple: rfq?.cargoInfo?.productCategory,
         rfq_number:                rfq?.rfqNumber,
         bound_type:                rfq?.rfqDetails?.boundType,
         contract_start_date:       rfq?.rfqDetails?.contractTenureFrom,
         contract_end_date:         rfq?.rfqDetails?.contractTenureTo,
      };
      setDphSaving((p) => ({ ...p, [idx]: true }));
      dispatch({
         type: UPDATE_DPH_CALCULATION_MASTER,
         data: payload,
         onSuccess: () => {
            setDphEditMode((p) => ({ ...p, [idx]: false }));
            setDphSaving((p) => ({ ...p, [idx]: false }));
         },
      });
   };

   const cancelDph = (idx, trip) => {
      const rfqTrip = rfqTripById[trip.id] || rfqTripById[trip.tripId];
      setDphData((p) => ({ ...p, [idx]: getDphFromRfqTrip(rfqTrip) }));
      setDphEditMode((p) => ({ ...p, [idx]: false }));
   };

   if (!trips || trips.length === 0) return null;

   // Detect whether bids carry rank info via orderText (e.g. "L1", "L2")
   const hasRankInfo = trips.some((t) =>
      (t.bids || []).some((b) => parseInt((b.orderText || '').replace(/\D/g, ''), 10) > 0)
   );

   // When rank info is present, column count = highest rank so each bid lands in its
   // correct Lx slot. Otherwise fall back to the maximum number of bids per trip.
   const maxColumns = hasRankInfo
      ? trips.reduce((max, t) => {
           (t.bids || []).forEach((b) => {
              const rank = parseInt((b.orderText || '').replace(/\D/g, ''), 10) || 0;
              if (rank > max) max = rank;
           });
           return max;
        }, 1)
      : Math.max(...trips.map((t) => (t.bids || []).length), 1);

   const bidLabels = Array.from({ length: maxColumns }, (_, i) => `L${i + 1}`);

   // Single shared grid template — header + all data rows use the same grid context
   // so every column has one authoritative width.
   const gridTemplate = [
      '32px',          // checkbox
      '200px',         // Line Items
      '110px',         // L1 Hits
      '160px',         // Reference
      ...bidLabels.map(() => `minmax(${BID_COL_MIN}px, 1fr)`),
      '110px',         // Bid Withdrawn
   ].join(' ');

   return (
      <div className="box-shadow ph-20 pv-20 br-5 bg-white mt-20 mb-20">
         <div className="border-bottom-light mb-30" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 40 }}>
            <div className="fBold fs-22">Bid Details</div>
            <div style={{ display: 'flex', gap: 8 }}>
               <button
                  onClick={handleDownloadDph}
                  style={{
                     display: 'flex', alignItems: 'center', gap: 6,
                     padding: '6px 14px', fontSize: 12, fontWeight: 600,
                     border: '1.5px solid #1b6de3', borderRadius: 6,
                     background: '#fff', color: '#1b6de3', cursor: 'pointer',
                  }}
               >
                  <FiDownload size={14} /> Download DPH
               </button>
               <input
                  ref={uploadInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  style={{ display: 'none' }}
                  onChange={handleUploadDph}
               />
               <button
                  onClick={() => uploadInputRef.current && uploadInputRef.current.click()}
                  style={{
                     display: 'flex', alignItems: 'center', gap: 6,
                     padding: '6px 14px', fontSize: 12, fontWeight: 600,
                     border: 'none', borderRadius: 6,
                     background: '#1b6de3', color: '#fff', cursor: 'pointer',
                  }}
               >
                  <FiUpload size={14} /> Upload DPH
               </button>
            </div>
         </div>

         <div style={{ overflowX: 'auto' }}>
            {/*
             * ONE flat grid — header cells and all data cells are direct children.
             * This is the only way to guarantee header columns stay aligned with
             * data columns when cell content varies in width.
             */}
            <div
               style={{
                  display: 'grid',
                  gridTemplateColumns: gridTemplate,
                  minWidth: 'max-content',
               }}
            >
               {/* ── HEADER ROW ─────────────────────────────────────────── */}
               <div style={HEADER_CELL} />
               <div style={HEADER_CELL}>Line Items</div>
               <div style={HEADER_CELL}>L1 Hits | Plain Hits</div>
               <div style={HEADER_CELL}>Reference</div>
               {bidLabels.map((label) => (
                  <div
                     key={`h-${label}`}
                     style={{ ...HEADER_CELL, display: 'flex', alignItems: 'center', gap: 4 }}
                  >
                     <input type="checkbox" style={{ cursor: 'pointer', flexShrink: 0 }} />
                     <span>{label}</span>
                  </div>
               ))}
               <div style={HEADER_CELL}>Bid Withdrawn</div>
               <div style={ROW_SEPARATOR} />

               {/* ── DATA ROWS ──────────────────────────────────────────── */}
               {trips.flatMap((trip, tripIdx) => {
                  const bids = trip.bids || [];

                  const businessVolume = trip.biddingDetails
                     ? `${trip.biddingDetails.businessVolumeValue || ''} ${trip.biddingDetails.businessVolumeUnitName || ''}`.trim()
                     : '';
                  const lppValue = trip.lpp != null ? trip.lpp : 0;
                  const vehicleDesc = [trip.vehicleTypeName, trip.vehicleBodyTypeName]
                     .filter(Boolean)
                     .join(' - ');

                  const key = (col) => `${tripIdx}-${col}`;

                  return [
                     /* checkbox */
                     <div key={key('cb')} style={DATA_CELL} className="flex flex-middle">
                        <input type="checkbox" style={{ cursor: 'pointer' }} />
                     </div>,

                     /* Line Items */
                     <div key={key('li')} style={DATA_CELL}>
                        <div className="fBold fs-13 header-dark-color mb-3">
                           {trip.from} - {trip.to}
                        </div>
                        {businessVolume && (
                           <div className="table-text-color mb-2">{businessVolume}</div>
                        )}
                        {vehicleDesc && (
                           <div className="table-text-color mb-2">{vehicleDesc}</div>
                        )}
                        {trip.distance && (
                           <div className="table-text-color mb-2">
                              {trip.distance} {trip.distanceUOM || 'KMS'}
                              {trip.isKmInsertedBy ? ` (${trip.isKmInsertedBy})` : ''}
                           </div>
                        )}
                        {isSapDPL() && (
                           <button
                              onClick={() => toggleDph(tripIdx)}
                              style={{
                                 marginTop: 4,
                                 background: 'none',
                                 border: 'none',
                                 color: '#1b6de3',
                                 cursor: 'pointer',
                                 padding: 0,
                                 fontSize: 12,
                                 fontWeight: 600,
                              }}
                           >
                              <FaGasPump size={12} style={{ marginRight: 4 }} />
                              DPH Details {openDph[tripIdx]}
                           </button>
                        )}
                     </div>,

                     /* L1 Hits | Plain Hits */
                     <div key={key('hits')} style={{ ...DATA_CELL, fontSize: 13 }} className="table-text-color">
                        {trip.l1Hits != null ? trip.l1Hits : 0} |{' '}
                        {trip.hits != null ? trip.hits : 0}
                     </div>,

                     /* Reference */
                     <div key={key('ref')} style={DATA_CELL} className="table-text-color">
                        <div className="mb-2">
                           Ceiling Price :{' '}
                           <span className="header-dark-color fBold">₹{lppValue}</span>
                        </div>
                        <div className="mb-2">LPP:</div>
                        {businessVolume && <div>Business Volume: {businessVolume}</div>}
                     </div>,

                     /* Bid columns */
                     ...bidLabels.map((label, idx) => {
                        const targetRank = idx + 1;
                        const bid = hasRankInfo
                           ? bids.find((b) => {
                                const rank =
                                   parseInt((b.orderText || '').replace(/\D/g, ''), 10) || 999;
                                return rank === targetRank;
                             })
                           : bids[idx];

                        if (!bid) return <div key={key(label)} style={DATA_CELL} />;

                        const editKey = `${trip.id}|${bid.transporterId}`;
                        const editedVal = editedPrices[editKey];
                        const displayPrice =
                           editedVal !== undefined
                              ? editedVal
                              : bid.price != null
                              ? bid.price
                              : '';

                        return (
                           <div key={key(label)} style={DATA_CELL}>
                              <div className="fBold fs-13 header-dark-color mb-4">
                                 {bid.transporterName}
                              </div>

                              <div
                                 className="flex flex-middle mb-5"
                                 style={{ gap: 4, flexWrap: 'wrap' }}
                              >
                                 <span className="table-text-color">Total Price : ₹</span>
                                 <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={displayPrice}
                                    onChange={(e) =>
                                       onPriceChange(trip.id, bid.transporterId, e.target.value)
                                    }
                                    style={{
                                       width: 90,
                                       border: '1px solid #d0d0d0',
                                       borderRadius: 4,
                                       padding: '2px 6px',
                                       fontSize: 13,
                                       outline: 'none',
                                    }}
                                    onFocus={(e) => (e.target.style.borderColor = '#1b6de3')}
                                    onBlur={(e) => (e.target.style.borderColor = '#d0d0d0')}
                                 />
                                 <span className="table-text-color">
                                    {trip.pricingBasisName || bid.pricingBasis || ''}
                                 </span>
                              </div>

                              <div className="mb-5" style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                 {bid.isLoiSent && bid.isAccept === true && (
                                    <span className="header-dark-color bg-accept" style={LOI_BADGE}>
                                       LOI Accepted
                                    </span>
                                 )}
                                 {bid.isLoiSent && bid.isAccept === false && (
                                    <span className="header-dark-color bg-reject" style={LOI_BADGE}>
                                       LOI Rejected
                                    </span>
                                 )}
                                 {bid.isLoiApproved && (
                                    <span className="header-dark-color bg-accept" style={LOI_BADGE}>
                                       LOI Approved
                                    </span>
                                 )}
                              </div>

                              {bid.tat && (
                                 <div className="table-text-color mb-3">
                                    TAT&nbsp;&nbsp;{bid.tat}
                                 </div>
                              )}

                              <div className="table-text-color mb-3">
                                 Seeker Remarks
                                 {bid.seekerRemarks ? `: ${bid.seekerRemarks}` : ''}
                              </div>

                              {bid.cancelLoiEnableorDisable && (
                                 <div
                                    className="fs-12 loi-cancelled-color fSemibold mb-3"
                                    style={{ cursor: 'default' }}
                                 >
                                    Cancel LOI
                                 </div>
                              )}

                              {bid.share_of_business_percentage != null &&
                                 bid.share_of_business_percentage !== '' && (
                                    <div className="mt-5">
                                       Share Of Business:{' '}
                                       <span className="fBold">
                                          {bid.share_of_business_percentage}
                                       </span>
                                    </div>
                                 )}
                           </div>
                        );
                     }),

                     /* Bid Withdrawn */
                     <div key={key('wd')} style={DATA_CELL} className="table-text-color">
                        {bids.some((b) => b.isWithDrawBid) ? 'Withdrawn' : ''}
                     </div>,

                     /* DPH Details accordion — DPL trips only */
                     ...(isSapDPL() && openDph[tripIdx]
                        ? [
                             <div
                                key={key('dph')}
                                style={{
                                   gridColumn: '1 / -1',
                                   padding: '12px 16px',
                                 //   backgroundColor: '#f4f7fc',
                                   borderTop: '1px solid #e0e0e0',
                                }}
                             >
                                <div
                                   style={{
                                      background: '#eef2fb',
                                      borderRadius: 12,
                                      padding: '14px 18px',
                                   }}
                                >
                                   {/* Top row: checkbox+label left, buttons right */}
                                   <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                         <input
                                            type="checkbox"
                                            id={`is_hsd_enabled_${tripIdx}`}
                                            checked={(dphData[tripIdx] || {}).is_hsd_enabled || false}
                                            onChange={(e) => updateDphField(tripIdx, 'is_hsd_enabled', e.target.checked)}
                                            disabled={!dphEditMode[tripIdx]}
                                            style={{
                                               width: 16,
                                               height: 16,
                                               cursor: dphEditMode[tripIdx] ? 'pointer' : 'default',
                                               accentColor: '#1b6de3',
                                            }}
                                         />
                                         <label
                                            htmlFor={`is_hsd_enabled_${tripIdx}`}
                                            style={{ fontSize: 13, color: '#1a1a2e', fontWeight: 700, cursor: 'pointer' }}
                                         >
                                            DPH Enable
                                         </label>
                                      </div>
                                      <div style={{ display: 'flex', gap: 8 }}>
                                         <button
                                            title="Edit"
                                            onClick={() => enterDphEdit(tripIdx)}
                                            disabled={dphEditMode[tripIdx]}
                                            style={{
                                               display: 'flex',
                                               alignItems: 'center',
                                               justifyContent: 'center',
                                               width: 34,
                                               height: 34,
                                               border: '1.5px solid #1b6de3',
                                               borderRadius: 8,
                                               background: '#fff',
                                               color: '#1b6de3',
                                               cursor: dphEditMode[tripIdx] ? 'not-allowed' : 'pointer',
                                               opacity: dphEditMode[tripIdx] ? 0.4 : 1,
                                            }}
                                         >
                                            <FiEdit2 size={15} />
                                         </button>
                                         <button
                                            title="Cancel"
                                            onClick={() => cancelDph(tripIdx, trip)}
                                            disabled={!dphEditMode[tripIdx]}
                                            style={{
                                               display: 'flex',
                                               alignItems: 'center',
                                               justifyContent: 'center',
                                               width: 34,
                                               height: 34,
                                               border: '1.5px solid #e53e3e',
                                               borderRadius: 8,
                                               background: '#fff',
                                               color: '#e53e3e',
                                               cursor: dphEditMode[tripIdx] ? 'pointer' : 'not-allowed',
                                               opacity: dphEditMode[tripIdx] ? 1 : 0.4,
                                            }}
                                         >
                                            ✕
                                         </button>
                                         <button
                                            title="Save"
                                            onClick={() => saveDph(tripIdx)}
                                            disabled={!dphEditMode[tripIdx] || dphSaving[tripIdx]}
                                            style={{
                                               display: 'flex',
                                               alignItems: 'center',
                                               justifyContent: 'center',
                                               width: 34,
                                               height: 34,
                                               border: 'none',
                                               borderRadius: 8,
                                               background: '#1b6de3',
                                               color: '#fff',
                                               cursor: (dphEditMode[tripIdx] && !dphSaving[tripIdx]) ? 'pointer' : 'not-allowed',
                                               opacity: (dphEditMode[tripIdx] && !dphSaving[tripIdx]) ? 1 : 0.4,
                                            }}
                                         >
                                            {dphSaving[tripIdx] ? '…' : <FiSave size={15} />}
                                         </button>
                                      </div>
                                   </div>

                                   {/* Input fields row */}
                                   <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                                      {DPH_FIELDS.map(({ field, label }) => (
                                         <div key={field} style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: '1 1 120px', minWidth: 110 }}>
                                            <label style={{ fontSize: 11, color: '#6b7280', fontWeight: 500 }}>
                                               {label}
                                            </label>
                                            <input
                                               type="number"
                                               value={(dphData[tripIdx] || {})[field] || ''}
                                               onChange={(e) => updateDphField(tripIdx, field, e.target.value)}
                                               readOnly={!dphEditMode[tripIdx]}
                                               style={{
                                                  border: '1px solid #d1d9f0',
                                                  borderRadius: 6,
                                                  padding: '6px 10px',
                                                  fontSize: 12,
                                                  outline: 'none',
                                                  background: dphEditMode[tripIdx] ? '#fff' : '#e8ecf7',
                                                  color: '#1a1a2e',
                                                  cursor: dphEditMode[tripIdx] ? 'text' : 'default',
                                                  width: '100%',
                                                  boxSizing: 'border-box',
                                               }}
                                               onFocus={(e) => { if (dphEditMode[tripIdx]) e.target.style.borderColor = '#1b6de3'; }}
                                               onBlur={(e) => (e.target.style.borderColor = '#d1d9f0')}
                                            />
                                         </div>
                                      ))}
                                   </div>
                                </div>
                             </div>,
                          ]
                        : []),

                     /* Full-width row separator — one line exactly at the bottom of the tallest cell */
                     <div key={key('sep')} style={ROW_SEPARATOR} />,
                  ];
               })}
            </div>
         </div>
      </div>
   );
};

export default AdminBidsTable;
